// src/pages/CancellationRefundPolicy.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalTradeName: "Académie ZEDRO",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const CANCELLATION_COPY = {
  es: {
    title: "Política de Cancelación y Reembolsos",
    updated: "Última actualización: 17 de julio de 2026",
    intro: (
      <>
        Esta Política explica las condiciones generales de cancelación,
        renovación, acceso y reembolso aplicables a las compras, productos
        digitales, membresías, suscripciones y demás servicios pagos ofrecidos
        por <strong>{OPERATOR.platformName}</strong>, plataforma operada por{" "}
        <strong>{OPERATOR.legalTradeName}</strong>.
      </>
    ),
    paragraphs: [
      <>
        Las condiciones específicas de cada oferta, incluyendo descripción,
        precio, moneda, impuestos, forma de pago, periodicidad, duración del
        acceso, prueba gratuita o promoción cuando corresponda, se presentan
        antes de confirmar la transacción.
      </>,
      <>
        Esta Política complementa los{" "}
        <Link to="/legal/terminos">
          Términos y Condiciones de Uso, Compra y Suscripción
        </Link>
        . Ninguna disposición limita los derechos obligatorios reconocidos por
        la legislación aplicable.
      </>,
    ],
    sections: [
      {
        title: "1. Alcance de esta Política",
        content: (
          <>
            Esta Política se aplica a las transacciones realizadas directamente
            con {OPERATOR.platformName}. Una oferta particular puede incluir
            condiciones adicionales o más favorables, las cuales se mostrarán
            antes de la compra y formarán parte de esa transacción.
          </>
        ),
        extra: (
          <>
            Cuando una compra se realice mediante una tienda o plataforma externa,
            también pueden aplicar sus procedimientos de pago y reembolso, sin
            perjuicio de los derechos que correspondan frente a{" "}
            {OPERATOR.legalTradeName}.
          </>
        ),
      },
      {
        title: "2. Información antes de confirmar una compra",
        content: (
          <>
            Antes de concluir una transacción, la persona puede revisar y aceptar
            las condiciones esenciales de la oferta. Estas pueden incluir la
            identidad del producto o servicio, el precio total, la moneda, los
            impuestos, los cargos periódicos o no periódicos, la duración, la
            fecha o forma de entrega, la política de cancelación y reembolso y,
            cuando corresponda, la renovación automática.
          </>
        ),
        extra: (
          <>
            Después de la transacción, se proporciona una confirmación
            electrónica que puede conservarse y que contiene o permite consultar
            la información contractual aplicable.
          </>
        ),
      },
      {
        title: "3. Membresías, suscripciones y renovación",
        content: (
          <>
            Una membresía o suscripción se renueva según la frecuencia y las
            condiciones mostradas antes de su contratación, hasta que la persona
            la cancele o el servicio termine conforme al contrato y a la ley.
          </>
        ),
        extra: (
          <>
            La cancelación puede solicitarse en cualquier momento y detiene las
            renovaciones y cobros futuros. Para evitar un nuevo cobro, debe
            completarse antes de que se procese la siguiente renovación.
          </>
        ),
      },
      {
        title: "4. Pruebas gratuitas y periodos promocionales",
        content: (
          <>
            Cuando una membresía incluya una prueba gratuita o un periodo
            promocional, antes de confirmarla se informan su duración, las
            condiciones de elegibilidad, el precio que aplicará después, la
            frecuencia de renovación y la fecha prevista del primer cobro.
          </>
        ),
        extra: (
          <>
            Si la persona cancela antes de finalizar una prueba gratuita, no se
            realizará el primer cobro. Cuando no sea elegible para una prueba o
            reactive una suscripción, el momento del cobro y las condiciones
            aplicables se muestran antes de la confirmación. Cuando la ley lo
            exija, se enviará un aviso escrito antes de que termine un periodo a
            precio reducido o promocional.
          </>
        ),
      },
      {
        title: "5. Cómo cancelar una membresía o suscripción",
        content: (
          <>
            La cancelación puede realizarse directamente desde{" "}
            <strong>Mi cuenta</strong>, mediante la opción disponible para
            cancelar la membresía o suscripción. La persona también puede
            solicitar asistencia escribiendo a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>
        ),
        extra: (
          <>
            No se exige indicar un motivo ni pagar una penalidad por detener
            renovaciones futuras. La solicitud se considera recibida cuando la
            cancelación queda confirmada en la cuenta o cuando el mensaje llega
            al canal de soporte.
          </>
        ),
      },
      {
        title: "6. Efecto de la cancelación",
        content: (
          <>
            Salvo que la oferta indique una condición más favorable o la ley
            exija un resultado distinto, una cancelación efectuada después de un
            cobro permite conservar el acceso hasta el final del periodo ya
            pagado. Después de esa fecha, el acceso asociado a la suscripción
            termina.
          </>
        ),
        extra: (
          <>
            La cancelación no otorga acceso a periodos futuros, contenidos no
            entregados ni funciones que no formen parte del periodo adquirido.
            Los registros o datos de cuenta se gestionan conforme a la{" "}
            <Link to="/legal/privacidad">Política de Privacidad</Link>.
          </>
        ),
      },
      {
        title: "7. Regla general de reembolsos",
        content: (
          <>
            Salvo que una oferta establezca una garantía o condición más
            favorable, los importes correctamente cobrados por un periodo de
            suscripción ya iniciado o por un producto digital ya entregado no se
            reembolsan únicamente por cambio de opinión, falta de uso o
            cancelación anticipada.
          </>
        ),
        extra: (
          <>
            No se realizan reembolsos proporcionales por días no utilizados,
            salvo cuando la ley, la oferta aplicable o una decisión expresa de{" "}
            {OPERATOR.legalTradeName} disponga lo contrario.
          </>
        ),
      },
      {
        title: "8. Productos digitales y compras de pago único",
        content: (
          <>
            En los productos digitales o servicios de pago único, las
            condiciones de entrega, acceso, descarga y reembolso se presentan
            antes de la compra. Una vez entregado o habilitado el contenido, el
            simple cambio de opinión no genera automáticamente un derecho a
            reembolso.
          </>
        ),
        extra: (
          <>
            Esta regla no afecta los derechos relacionados con un producto o
            servicio no entregado, sustancialmente diferente de lo ofrecido,
            defectuoso, cobrado incorrectamente o sujeto a un derecho legal de
            cancelación.
          </>
        ),
      },
      {
        title: "9. Situaciones que requieren revisión",
        content: (
          <>
            La persona debe contactar a soporte cuando considere que existe un
            cobro duplicado, un error técnico, una transacción no autorizada, un
            importe incorrecto, un producto o servicio no recibido, una
            interrupción sustancial atribuible a la plataforma o cualquier otra
            situación que pueda justificar una corrección o reembolso.
          </>
        ),
        extra: (
          <>
            Cada solicitud se revisa según la transacción, la evidencia
            disponible, las condiciones aceptadas y la legislación aplicable.
            Cuando proceda, se realizará la corrección, restitución, extensión de
            acceso u otra solución apropiada.
          </>
        ),
      },
      {
        title: "10. Derechos aplicables a los contratos celebrados en línea",
        content: (
          <>
            Las personas consumidoras conservan los derechos de cancelación y
            reembolso previstos por la ley cuando, entre otras situaciones, no se
            proporcione correctamente la información obligatoria, no se entregue
            una copia conservable del contrato dentro del plazo legal o no se
            preste el bien o servicio en el plazo aplicable.
          </>
        ),
        extra: (
          <>
            Cuando un contrato celebrado a distancia sea cancelado válidamente
            en virtud de la ley, {OPERATOR.legalTradeName} reembolsará las sumas
            exigibles dentro del plazo legal. Esta Política no sustituye ni
            restringe esos recursos.
          </>
        ),
      },
      {
        title: "11. Procesamiento de reembolsos",
        content: (
          <>
            Los reembolsos aprobados se envían, por regla general, al medio de
            pago original mediante Stripe u otro proveedor utilizado para la
            transacción. Una vez emitido el reembolso, el tiempo necesario para
            que aparezca en la cuenta depende del proveedor de pago y de la
            institución financiera.
          </>
        ),
        extra: (
          <>
            Se puede solicitar información razonable para identificar la
            transacción y prevenir fraude. Nunca debe enviarse por correo el
            número completo de una tarjeta ni su código de seguridad.
          </>
        ),
      },
      {
        title: "12. Suspensión o terminación por ZEDRO Academy",
        content: (
          <>
            {OPERATOR.legalTradeName} puede suspender o terminar un acceso por
            fraude, uso no autorizado, incumplimiento grave, riesgo de seguridad,
            imposibilidad legal u otras causas permitidas por los Términos y la
            ley.
          </>
        ),
        extra: (
          <>
            La existencia y el alcance de un reembolso dependerán de la causa,
            del servicio efectivamente prestado y de los derechos obligatorios
            aplicables. Esta disposición no permite cancelar arbitrariamente una
            obligación ya asumida.
          </>
        ),
      },
      {
        title: "13. Cambios en esta Política",
        content: (
          <>
            Esta Política puede actualizarse para reflejar cambios legales,
            operativos, tecnológicos o en las ofertas disponibles. La versión
            vigente es la publicada en el sitio con su fecha de actualización.
          </>
        ),
        extra: (
          <>
            Una modificación posterior no altera retroactivamente las
            condiciones de una transacción ya concluida ni un periodo ya pagado,
            salvo acuerdo válido o exigencia legal.
          </>
        ),
      },
      {
        title: "14. Contacto",
        content: (
          <>
            Para cancelaciones, pagos, reembolsos o acceso, escribe a{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            . Para asuntos administrativos o legales, escribe a{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
            .
          </>
        ),
      },
      {
        title: "15. Documentos relacionados",
        content: (
          <>
            Esta Política complementa los{" "}
            <Link to="/legal/terminos">
              Términos y Condiciones de Uso, Compra y Suscripción
            </Link>
            , la <Link to="/legal/privacidad">Política de Privacidad</Link>, la{" "}
            <Link to="/legal/cookies">Política de Cookies</Link>, el{" "}
            <Link to="/legal/aviso-legal">Aviso legal</Link> y el{" "}
            <Link to="/legal/descargo">Descargo de responsabilidad</Link>.
          </>
        ),
      },
    ],
  },

  fr: {
    title: "Politique d’annulation et de remboursement",
    updated: "Dernière mise à jour : 17 juillet 2026",
    intro: (
      <>
        La présente Politique explique les conditions générales d’annulation, de
        renouvellement, d’accès et de remboursement applicables aux achats,
        produits numériques, adhésions, abonnements et autres services payants
        offerts par <strong>{OPERATOR.platformName}</strong>, une plateforme
        exploitée par <strong>{OPERATOR.legalTradeName}</strong>.
      </>
    ),
    paragraphs: [
      <>
        Les conditions propres à chaque offre, notamment la description, le
        prix, la devise, les taxes, le mode de paiement, la fréquence, la durée
        de l’accès, l’essai gratuit ou la promotion, le cas échéant, sont
        présentées avant la confirmation de la transaction.
      </>,
      <>
        La présente Politique complète les{" "}
        <Link to="/legal/terminos">
          Conditions d’utilisation, d’achat et d’abonnement
        </Link>
        . Aucune disposition ne limite les droits impératifs reconnus par la
        législation applicable.
      </>,
    ],
    sections: [
      {
        title: "1. Portée de la présente Politique",
        content: (
          <>
            La présente Politique s’applique aux transactions conclues
            directement avec {OPERATOR.platformName}. Une offre particulière
            peut prévoir des conditions supplémentaires ou plus favorables,
            lesquelles sont présentées avant l’achat et font partie de cette
            transaction.
          </>
        ),
        extra: (
          <>
            Lorsqu’un achat est effectué au moyen d’une boutique ou d’une
            plateforme externe, ses procédures de paiement et de remboursement
            peuvent également s’appliquer, sans préjudice des droits pouvant être
            exercés auprès d’{OPERATOR.legalTradeName}.
          </>
        ),
      },
      {
        title: "2. Renseignements avant la confirmation d’un achat",
        content: (
          <>
            Avant de conclure une transaction, la personne peut examiner et
            accepter les conditions essentielles de l’offre. Celles-ci peuvent
            comprendre l’identité du produit ou du service, le prix total, la
            devise, les taxes, les frais périodiques ou non périodiques, la
            durée, la date ou le mode de livraison, la politique d’annulation et
            de remboursement et, le cas échéant, le renouvellement automatique.
          </>
        ),
        extra: (
          <>
            Après la transaction, une confirmation électronique pouvant être
            conservée est fournie. Elle contient les renseignements contractuels
            applicables ou permet de les consulter.
          </>
        ),
      },
      {
        title: "3. Adhésions, abonnements et renouvellement",
        content: (
          <>
            Une adhésion ou un abonnement se renouvelle selon la fréquence et les
            conditions présentées avant sa souscription, jusqu’à ce que la
            personne l’annule ou que le service prenne fin conformément au
            contrat et à la loi.
          </>
        ),
        extra: (
          <>
            L’annulation peut être demandée en tout temps et met fin aux
            renouvellements et paiements futurs. Afin d’éviter un nouveau
            paiement, elle doit être effectuée avant le traitement du prochain
            renouvellement.
          </>
        ),
      },
      {
        title: "4. Essais gratuits et périodes promotionnelles",
        content: (
          <>
            Lorsqu’une adhésion comprend un essai gratuit ou une période
            promotionnelle, sa durée, les conditions d’admissibilité, le prix
            applicable par la suite, la fréquence de renouvellement et la date
            prévue du premier paiement sont indiqués avant la confirmation.
          </>
        ),
        extra: (
          <>
            Si la personne annule avant la fin d’un essai gratuit, aucun premier
            paiement n’est effectué. Lorsqu’elle n’est pas admissible à un essai
            ou réactive un abonnement, le moment du paiement et les conditions
            applicables sont affichés avant la confirmation. Lorsque la loi
            l’exige, un avis écrit est transmis avant la fin d’une période à prix
            réduit ou promotionnel.
          </>
        ),
      },
      {
        title: "5. Comment annuler une adhésion ou un abonnement",
        content: (
          <>
            L’annulation peut être effectuée directement dans{" "}
            <strong>Mon compte</strong>, au moyen de l’option permettant
            d’annuler l’adhésion ou l’abonnement. La personne peut aussi demander
            de l’aide en écrivant à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            .
          </>
        ),
        extra: (
          <>
            Aucun motif ni aucune pénalité ne sont exigés pour mettre fin aux
            renouvellements futurs. La demande est considérée comme reçue lorsque
            l’annulation est confirmée dans le compte ou lorsque le message
            parvient au service à la clientèle.
          </>
        ),
      },
      {
        title: "6. Effet de l’annulation",
        content: (
          <>
            À moins qu’une offre ne prévoie une condition plus favorable ou que
            la loi n’exige un résultat différent, une annulation effectuée après
            un paiement permet de conserver l’accès jusqu’à la fin de la période
            déjà payée. L’accès lié à l’abonnement prend ensuite fin.
          </>
        ),
        extra: (
          <>
            L’annulation ne donne aucun accès aux périodes futures, aux contenus
            non livrés ni aux fonctions qui ne font pas partie de la période
            achetée. Les registres et données du compte sont gérés conformément à
            la <Link to="/legal/privacidad">Politique de confidentialité</Link>.
          </>
        ),
      },
      {
        title: "7. Règle générale de remboursement",
        content: (
          <>
            À moins qu’une offre ne prévoie une garantie ou une condition plus
            favorable, les sommes correctement facturées pour une période
            d’abonnement déjà commencée ou un produit numérique déjà livré ne
            sont pas remboursées uniquement en raison d’un changement d’idée,
            d’une absence d’utilisation ou d’une annulation anticipée.
          </>
        ),
        extra: (
          <>
            Aucun remboursement proportionnel n’est accordé pour les jours non
            utilisés, sauf lorsque la loi, l’offre applicable ou une décision
            expresse d’{OPERATOR.legalTradeName} prévoit le contraire.
          </>
        ),
      },
      {
        title: "8. Produits numériques et achats à paiement unique",
        content: (
          <>
            Pour les produits numériques ou services à paiement unique, les
            conditions de livraison, d’accès, de téléchargement et de
            remboursement sont présentées avant l’achat. Une fois le contenu
            livré ou rendu accessible, un simple changement d’idée ne crée pas
            automatiquement un droit au remboursement.
          </>
        ),
        extra: (
          <>
            Cette règle ne porte pas atteinte aux droits liés à un produit ou
            service non livré, substantiellement différent de ce qui a été
            offert, défectueux, facturé incorrectement ou visé par un droit légal
            d’annulation.
          </>
        ),
      },
      {
        title: "9. Situations nécessitant un examen",
        content: (
          <>
            La personne doit communiquer avec le service à la clientèle si elle
            croit qu’il y a eu une facturation en double, une erreur technique,
            une transaction non autorisée, un montant incorrect, un produit ou
            service non reçu, une interruption importante attribuable à la
            plateforme ou toute autre situation pouvant justifier une correction
            ou un remboursement.
          </>
        ),
        extra: (
          <>
            Chaque demande est examinée selon la transaction, les éléments de
            preuve disponibles, les conditions acceptées et la législation
            applicable. Lorsqu’il y a lieu, une correction, une restitution, une
            prolongation d’accès ou une autre solution appropriée est accordée.
          </>
        ),
      },
      {
        title: "10. Droits applicables aux contrats conclus en ligne",
        content: (
          <>
            Les consommateurs conservent les droits d’annulation et de
            remboursement prévus par la loi lorsque, notamment, les
            renseignements obligatoires ne sont pas correctement fournis, une
            copie du contrat pouvant être conservée n’est pas transmise dans le
            délai légal ou le bien ou service n’est pas fourni dans le délai
            applicable.
          </>
        ),
        extra: (
          <>
            Lorsqu’un contrat conclu à distance est valablement annulé en vertu
            de la loi, {OPERATOR.legalTradeName} rembourse les sommes exigibles
            dans le délai légal. La présente Politique ne remplace ni ne
            restreint ces recours.
          </>
        ),
      },
      {
        title: "11. Traitement des remboursements",
        content: (
          <>
            Les remboursements approuvés sont généralement transmis au mode de
            paiement original par Stripe ou par l’autre fournisseur utilisé pour
            la transaction. Une fois le remboursement émis, le délai nécessaire
            pour qu’il apparaisse au compte dépend du fournisseur de paiement et
            de l’institution financière.
          </>
        ),
        extra: (
          <>
            Des renseignements raisonnables peuvent être demandés afin
            d’identifier la transaction et de prévenir la fraude. Le numéro
            complet d’une carte et son code de sécurité ne doivent jamais être
            transmis par courriel.
          </>
        ),
      },
      {
        title: "12. Suspension ou fin du service par ZEDRO Academy",
        content: (
          <>
            {OPERATOR.legalTradeName} peut suspendre ou mettre fin à un accès en
            cas de fraude, d’utilisation non autorisée, de manquement grave, de
            risque pour la sécurité, d’impossibilité légale ou pour toute autre
            cause permise par les Conditions et la loi.
          </>
        ),
        extra: (
          <>
            L’existence et l’étendue d’un remboursement dépendent de la cause, du
            service effectivement fourni et des droits impératifs applicables.
            La présente disposition ne permet pas de mettre fin arbitrairement à
            une obligation déjà assumée.
          </>
        ),
      },
      {
        title: "13. Modifications de la présente Politique",
        content: (
          <>
            La présente Politique peut être mise à jour afin de refléter des
            changements juridiques, opérationnels, technologiques ou apportés aux
            offres disponibles. La version en vigueur est celle publiée sur le
            site avec sa date de mise à jour.
          </>
        ),
        extra: (
          <>
            Une modification ultérieure ne change pas rétroactivement les
            conditions d’une transaction déjà conclue ni d’une période déjà
            payée, sauf entente valide ou exigence légale.
          </>
        ),
      },
      {
        title: "14. Coordonnées",
        content: (
          <>
            Pour les annulations, paiements, remboursements ou questions d’accès,
            écrivez à{" "}
            <a href={`mailto:${OPERATOR.supportEmail}`}>
              {OPERATOR.supportEmail}
            </a>
            . Pour les questions administratives ou juridiques, écrivez à{" "}
            <a href={`mailto:${OPERATOR.adminEmail}`}>
              {OPERATOR.adminEmail}
            </a>
            .
          </>
        ),
      },
      {
        title: "15. Documents connexes",
        content: (
          <>
            La présente Politique complète les{" "}
            <Link to="/legal/terminos">
              Conditions d’utilisation, d’achat et d’abonnement
            </Link>
            , la <Link to="/legal/privacidad">Politique de confidentialité</Link>,
            la <Link to="/legal/cookies">Politique relative aux témoins</Link>,
            l’<Link to="/legal/aviso-legal">Avis juridique</Link> et l’{" "}
            <Link to="/legal/descargo">Avis de non-responsabilité</Link>.
          </>
        ),
      },
    ],
  },
};

export default function CancellationRefundPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = CANCELLATION_COPY[language] || CANCELLATION_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      <p>{copy.intro}</p>

      {copy.paragraphs.map((paragraph, index) => (
        <p key={`paragraph-${index}`}>{paragraph}</p>
      ))}

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              color: "#264763",
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
            }}
          >
            {section.title}
          </h2>

          <div>{section.content}</div>
          {section.extra && <p>{section.extra}</p>}
        </section>
      ))}
    </main>
  );
}
